import { getGas } from './gasDatabase';

export type GasTest =
  | 'lit-splint'
  | 'glowing-splint'
  | 'limewater'
  | 'damp-red-litmus'
  | 'damp-blue-litmus'
  | 'white-fumes'
  | 'dichromate'
  | 'lead-acetate'
  | 'indicator'
  | 'waft-smell';

export interface TestOutcome {
  test: GasTest;
  gasId: string;
  result: 'positive' | 'negative' | 'warning';
  observation: string;
  note?: string;
}

const testOutcomeMap: Record<string, Record<string, TestOutcome>> = {
  h2: {
    'lit-splint': { test: 'lit-splint', gasId: 'h2', result: 'positive', observation: 'A squeaky pop is heard as hydrogen burns.' },
    'glowing-splint': { test: 'glowing-splint', gasId: 'h2', result: 'negative', observation: 'The glowing splint is extinguished.' },
  },
  o2: {
    'glowing-splint': { test: 'glowing-splint', gasId: 'o2', result: 'positive', observation: 'The glowing splint relights with a brighter glow.' },
    'lit-splint': { test: 'lit-splint', gasId: 'o2', result: 'positive', observation: 'The flame burns more vigorously.' },
  },
  co2: {
    limewater: { test: 'limewater', gasId: 'co2', result: 'positive', observation: 'Limewater turns milky because calcium carbonate forms.' },
    'lit-splint': { test: 'lit-splint', gasId: 'co2', result: 'negative', observation: 'The flame is extinguished.' },
  },
  nh3: {
    'damp-red-litmus': { test: 'damp-red-litmus', gasId: 'nh3', result: 'positive', observation: 'Damp red litmus turns blue.' },
    'white-fumes': { test: 'white-fumes', gasId: 'nh3', result: 'positive', observation: 'Dense white fumes form when the gas meets hydrogen chloride vapour.' },
  },
  cl2: {
    'damp-blue-litmus': { test: 'damp-blue-litmus', gasId: 'cl2', result: 'positive', observation: 'bleaches damp blue litmus paper.' },
    'damp-red-litmus': { test: 'damp-red-litmus', gasId: 'cl2', result: 'positive', observation: 'bleaches damp red litmus paper and may turn it white.' },
  },
  so2: {
    dichromate: { test: 'dichromate', gasId: 'so2', result: 'positive', observation: 'Acidified potassium dichromate paper turns green.' },
    'damp-blue-litmus': { test: 'damp-blue-litmus', gasId: 'so2', result: 'positive', observation: 'Blue litmus turns red.' },
  },
  hcl: {
    'damp-blue-litmus': { test: 'damp-blue-litmus', gasId: 'hcl', result: 'positive', observation: 'Damp blue litmus turns red.' },
    'white-fumes': { test: 'white-fumes', gasId: 'hcl', result: 'positive', observation: 'White fumes form when ammonia gas meets hydrogen chloride.' },
  },
  no2: {
    'damp-blue-litmus': { test: 'damp-blue-litmus', gasId: 'no2', result: 'positive', observation: 'Damp blue litmus turns red.' },
    'waft-smell': { test: 'waft-smell', gasId: 'no2', result: 'warning', observation: 'Sharp, irritating smell; use a fume cupboard and waft gently.' },
  },
  n2: {
    'lit-splint': { test: 'lit-splint', gasId: 'n2', result: 'negative', observation: 'The splint is extinguished and no pop is heard.' },
    'glowing-splint': { test: 'glowing-splint', gasId: 'n2', result: 'negative', observation: 'The glowing splint goes out.' },
  },
  h2s: {
    'lead-acetate': { test: 'lead-acetate', gasId: 'h2s', result: 'positive', observation: 'Lead acetate paper turns black due to PbS formation.' },
    'waft-smell': { test: 'waft-smell', gasId: 'h2s', result: 'warning', observation: 'Rotten egg smell; waft gently and use a fume cupboard.' },
  },
};

export function getTestOutcome(gasId: string, test: GasTest): TestOutcome {
  const gas = getGas(gasId);
  const outcome = (testOutcomeMap[gas.id] || {})[test];
  if (outcome) return outcome;
  return {
    test,
    gasId: gas.id,
    result: 'negative',
    observation: `No characteristic observable is expected for ${gas.name} with ${test}.`,
  };
}
